import { useState, useEffect } from 'react'
import s from './ReportByMissingTime.scss'
import { makeReportsPostRequest, makeGetRequest, getReportsBaseUrl } from 'utils/api'
import { useLoading } from 'hooks'
import Select from 'react-select'
import { RangeDatePicker } from 'components/ideal-core-ui'
import { utcDate, timezoneDate } from 'utils/helpers'
import moment from 'moment'
import { toUsers } from '../serializers'
import { isTimetrackerAdmin, getToken } from 'utils/authService'
import { AddButton } from 'components/common/AddButton'
import { FaAngleDown, FaFileExcel, FaAngleUp } from 'react-icons/fa'
import { Table } from 'reactstrap'
import { toMissingData } from './serializers'

const ReportByMissingTime = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  handleDatesShortcut,
  dateRangeOption,
  setDateRangeOption
}) => {
  const [, withLoadingLocks] = useLoading()
  const [userFilter, setUserFilter] = useState([])
  const [result, setResult] = useState(null)
  const [users, setUsers] = useState([])
  const [expandedSections, setExpandedSections] = useState([])

  const setDateFilters = (dates, utc = true) => {
    setStartDate(utc ? utcDate(dates.from) : dates.from)
    setEndDate(utc ? utcDate(dates.to) : dates.to)
  }

  useEffect(() => {
    ;(async () => {
      if (!isTimetrackerAdmin()) return
      const { content } = await makeGetRequest('/v1/timetracker/users')
      setUsers(toUsers(content))
    })()
  }, [])

  useEffect(() => {
    ;(async () => {
      const response = await withLoadingLocks(
        makeReportsPostRequest('/v1/reports/time-tracker/missing-time', {
          from: startDate,
          to: endDate,
          ...(userFilter && userFilter.length
            ? { userFilter: userFilter.map(user => user.value) }
            : {})
        })
      )
      setResult(response)
      setExpandedSections(
        Object.keys(response).map(() => {
          return false
        })
      )
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, userFilter])

  const getDateRangeLabel = () => {
    if (!startDate || !endDate) return ''
    const formattedStartDate = timezoneDate(
      startDate,
      undefined,
      undefined,
      undefined,
      'MMMM D, YYYY'
    ).toUpperCase()
    const formattedEndDate = timezoneDate(
      endDate,
      undefined,
      undefined,
      undefined,
      'MMMM D, YYYY'
    ).toUpperCase()

    const showYear = moment(startDate).year() !== moment(endDate).year()

    let dateRange
    if (showYear) {
      dateRange = `${formattedStartDate} - ${formattedEndDate}`
    } else {
      dateRange = `${timezoneDate(
        startDate,
        undefined,
        undefined,
        undefined,
        'MMMM D'
      ).toUpperCase()} - ${timezoneDate(
        endDate,
        undefined,
        undefined,
        undefined,
        'MMMM D, YYYY'
      ).toUpperCase()}`
    }

    return dateRange
  }

  const onBtnExport = () => {
    window.open(
      getReportsBaseUrl(
        `/v1/reports/time-tracker/missing-time/export?userFilter=${userFilter
          .map(user => user.value)
          .join()}&token=${getToken()}${'&from=' + startDate + '&to=' + endDate}`
      ),
      '_blank'
    )
  }

  const expandSection = index => {
    const newExpandedSections = [...expandedSections]
    newExpandedSections[index] = !newExpandedSections[index]
    setExpandedSections(newExpandedSections)
  }

  return (
    <div className={s.container}>
      <div className={s.filters}>
        <div className={s.filter}>
          <Select
            className={s.dateRangeSelect}
            onChange={v => {
              handleDatesShortcut(v)
              setDateRangeOption(v)
            }}
            options={[
              { value: 'lastYear', label: 'Last Year' },
              { value: 'thisYear', label: 'This Year' },
              { value: 'lastMonth', label: 'Last Month' },
              { value: 'thisMonth', label: 'This Month' },
              { value: 'lastWeek', label: 'Last Week' },
              { value: 'thisWeek', label: 'This Week' },
              { value: 'custom', label: 'Custom Dates' }
            ]}
            value={dateRangeOption}
            placeholder="Select Date Range"
          />
          {dateRangeOption.value === 'custom' && (
            <RangeDatePicker startDate={startDate} endDate={endDate} onChange={setDateFilters} />
          )}
        </div>
        {isTimetrackerAdmin() && (
          <Select
            onChange={values => setUserFilter(values)}
            options={users}
            value={userFilter}
            placeholder="Select User(s)"
            className={s.userSelect}
            isMulti
          />
        )}
      </div>
      {result && Object.entries(result).length ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <p className={s.dateRange}>{getDateRangeLabel()}</p>
            <AddButton className={s.exportExcel} onClick={onBtnExport}>
              <FaFileExcel className={s.excelIcon} />
              Export to Excel
            </AddButton>
          </div>
          <hr />
          {Object.entries(result).map(([key, value], index) => {
            const result = toMissingData(value)

            return (
              <div key={key} className={s.userStats}>
                <p className={s.entriesFor} onClick={() => expandSection(index)}>
                  {key}
                  {expandedSections[index] ? <FaAngleUp /> : <FaAngleDown />}
                </p>
                <div style={{ borderBottom: '1px solid gray', paddingBottom: 20 }}>
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}
                    >
                      <span style={{ fontWeight: 600, color: 'gray' }}>Missing hours</span>
                      <span style={{ fontSize: 18, fontWeight: 'bold' }}>
                        {result.missingHours.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
                {expandedSections[index] &&
                  result.data.map((entry, index) => {
                    return (
                      <div key={index} className={s.dayStats}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between'
                          }}
                        >
                          <span style={{ fontWeight: 600 }}>
                            Week {entry.weekStart} - {entry.weekEnd}
                          </span>
                          <span style={{ fontSize: 18 }}>
                            {entry.hoursPerWeek - entry.loggedHours} hrs
                          </span>
                        </div>
                        <hr />
                        <Table bordered style={{ width: '500px' }}>
                          <thead>
                            <tr>
                              <th style={{ width: '20%' }}>Day</th>
                              <th style={{ width: '20%' }}>Hours</th>
                            </tr>
                          </thead>
                          <tbody>
                            {entry.missingTime.map((day, index) => {
                              return (
                                <tr key={index}>
                                  <td style={{ width: '20%' }}>{day.workDate}</td>
                                  <td style={{ width: '20%' }}>{day.hours}</td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </Table>
                      </div>
                    )
                  })}
              </div>
            )
          })}
        </>
      ) : (
        <div className={s.noData}>No data. Please check your filters.</div>
      )}
    </div>
  )
}

export default ReportByMissingTime
